/** The draft route's live map layers — the one thing on this map that must
 * survive map.setStyle(): App swaps the entire FirstLight style on every
 * look/night/sun change (including a live-sun refresh every 10 minutes —
 * see App.tsx's useLiveSun), and setStyle wipes every runtime source/layer.
 *
 * ensureRouteOverlay is idempotent — source/layer exists -> setData;
 * missing -> addSource/addLayer — so it's safe to call it from BOTH a
 * "styledata" listener (after every style swap, reading the latest view off
 * a ref) and on every React state change, exactly the way MapCanvas already
 * keeps layer toggles alive across a style swap for applyToggles (see
 * map/MapCanvas.tsx ~line 79: `map.on("styledata", () => applyToggles(...))`).
 * A style is briefly not-ready right after setStyle(); isStyleLoaded() plus
 * a try/catch mean a call during that window just no-ops, and the next
 * "styledata" tick (there are usually several per swap) tries again.
 *
 * The snap-to-trail additions (ghost hover point, magnetize flash) live in
 * this same module and go through the SAME ensureSourcesAndLayers — a style
 * swap mid-draw must not silently drop them the way a lazily-created layer
 * would, so their sources/layers are built proactively every ensure pass,
 * not on first use by flashSnap/setPlanGhost. */

import type maplibregl from "maplibre-gl";
import { DEFAULT_ROUTE_LOOK, ROUTE_COLORS, ROUTE_WEIGHTS, type RouteLook, type RouteWeight } from "./routeLook";
import { DEFAULT_MARKINGS, MARKINGS, type MapMarkings } from "../map/mapMarkings";
import type { LegMode, LonLat } from "./types";

/** Rendering input: the caller (App.tsx) already knows whether draw mode is
 * active and has the plan's per-leg geometry — this module only ever paints
 * what it's handed.
 *
 * The five fields below are OPTIONAL — added for day breaks / seasonal
 * closures / grade coloring / hover-scrub ahead of the App.tsx wave that
 * wires them up, so every existing caller keeps compiling and rendering
 * exactly as before with them simply absent. */
export interface RouteOverlayView {
  legs: RouteOverlayLeg[];
  points: LonLat[];
  waypoints: { at: LonLat; name: string }[];
  selected: number | null;
  active: boolean;
  /** Overnight markers, one per plan.dayBreaks entry — already resolved to
   * a coordinate + label (e.g. "Day 2") by the caller. Absent/empty draws
   * none. */
  dayBreaks?: { at: LonLat; label: string }[];
  /** Grade-coloring toggle — see ensureRouteOverlay's visibility contract
   * for exactly how this and gradeRuns interact with ROUTE_LINE_LAYER. */
  gradeOn?: boolean;
  /** Grade-classified runs along the route geometry (plan/grade.ts's
   * gradeRuns) — only painted while gradeOn is true. */
  gradeRuns?: { cls: string; coords: LonLat[] }[];
  /** Seasonally-restricted polylines this plan's snapped legs traverse
   * (plan/seasons.ts's closureWarnings) — painted as a two-layer barrier
   * hatch under the route (see the color-semantics comment above
   * CLOSURE_SLEEVE_COLOR for why it's a pattern, not a color). */
  closures?: LonLat[][];
}

/** One leg's renderable geometry: its own endpoint, via[], endpoint —
 * NOT deduped against neighboring legs, so each leg is one independent
 * LineString feature (see legsFC) rather than one flattened line. */
export interface RouteOverlayLeg {
  mode: LegMode;
  coords: LonLat[];
}

export const ROUTE_LINE_SOURCE = "plan-line";
export const ROUTE_POINTS_SOURCE = "plan-points";
export const ROUTE_LINE_CASING_LAYER = "plan-line-casing";
/** The dark outer outline UNDER the cream casing (Sep 6 desktop legibility
 * pass) — soilink at 0.55, casing + 2 wide, filtered to mode:"snapped"
 * like the solid line. Measured on the live map: trace blue vs the BLM
 * road blue (#3E6E93) is 1.01:1 and vs MVUM ember 1.26:1, and the 1px of
 * cream casing that peeked past the old 3.5px line was 1.06:1 against the
 * cream ground — it added nothing there. Those numbers were measured
 * against the cobalt the route was then always painted in; the conclusion
 * they support is the reason this layer OUTLIVED that: width plus a DARK
 * outer edge separates the route from every road it overlays at ANY hue,
 * which is exactly what a rider-chosen colour needs (Sep 14 2026, see
 * plan/routeLook.ts). The hue is now his; the dark edge under it is not,
 * and that is the point. Snapped legs only, on purpose: the
 * roads it contrasts against are exactly where snapped legs run, while a
 * straight (off-network) leg crosses open ground — and a continuous dark
 * band under a dashed line reads as a solid path with a dashed centerline,
 * i.e. the road symbol, which would mute the very "not on the network"
 * cue the dashes exist for (docs/WEB_PLANNER_UX_REVIEW.md). Straight legs
 * keep only the cream casing they always had. */
export const ROUTE_LINE_OUTLINE_LAYER = "plan-line-outline";
/** The solid, on-network layer — filtered to mode:"snapped" features. */
export const ROUTE_LINE_LAYER = "plan-line";
/** The honestly-dashed, off-network layer — filtered to mode:"straight".
 * A second layer over the SAME source rather than a data-driven dasharray:
 * MapLibre's line-dasharray isn't a data-driven paint property. */
export const ROUTE_LINE_STRAIGHT_LAYER = "plan-line-straight";
/** Exported so App.tsx's click/drag hit-testing (queryRenderedFeatures)
 * targets exactly the layer this module actually draws. */
export const ROUTE_POINTS_LAYER = "plan-points";
/** A soft ring painted UNDER the selected control point — the point
 * layer's own selected-vs-not radius delta (see below) reads as noise at a
 * glance, especially on a phone with a finger over it. */
export const ROUTE_POINTS_HALO_LAYER = "plan-points-halo";

/** Named landmarks along the route (Gate, Water crossing, …) — distinct
 * from the draggable control points above. Exported so App.tsx's
 * right-click hit-testing (queryRenderedFeatures) targets exactly the
 * layer this module draws, same reasoning as ROUTE_POINTS_LAYER. */
export const ROUTE_WAYPOINTS_SOURCE = "plan-waypoints";
export const ROUTE_WAYPOINTS_LAYER = "plan-waypoints";
const ROUTE_WAYPOINTS_LABEL_LAYER = "plan-waypoints-label";

const ROUTE_GHOST_SOURCE = "plan-ghost";
const ROUTE_GHOST_LAYER = "plan-ghost";
const ROUTE_FLASH_SOURCE = "plan-flash";
const ROUTE_FLASH_LAYER = "plan-flash";

/** The seasonally-restricted ways a plan's snapped legs traverse — painted
 * as a two-layer barrier hatch under the route (see ensureSourcesAndLayers'
 * stacking comment): ROUTE_CLOSURES_LAYER is the dark sleeve under-layer,
 * ROUTE_CLOSURES_TIE_LAYER the light dashed ties painted directly over it
 * on this SAME source. */
const ROUTE_CLOSURES_SOURCE = "plan-closures";
const ROUTE_CLOSURES_LAYER = "plan-closures";
/** The hatch's light dashed over-layer — see ROUTE_CLOSURES_SOURCE's own
 * comment above. */
const ROUTE_CLOSURES_TIE_LAYER = "plan-closures-ties";
/** The grade-colored replacement for ROUTE_LINE_LAYER — see
 * ensureRouteOverlay's visibility contract for how the two trade off. */
const ROUTE_GRADE_SOURCE = "plan-grade";
const ROUTE_GRADE_LAYER = "plan-grade";
/** Overnight markers — a circle plus its own label sublayer, same split as
 * ROUTE_WAYPOINTS_LAYER/ROUTE_WAYPOINTS_LABEL_LAYER. */
const ROUTE_DAYBREAKS_SOURCE = "plan-daybreaks";
const ROUTE_DAYBREAKS_LAYER = "plan-daybreaks";
const ROUTE_DAYBREAKS_LABEL_LAYER = "plan-daybreaks-label";
/** ProfileStrip's hover-scrub cursor, mirrored onto the map. */
const ROUTE_SCRUB_SOURCE = "plan-scrub";
const ROUTE_SCRUB_LAYER = "plan-scrub";

const CASING_COLOR = "#fbf6ea"; // night-safe contrast halo
const TRACE_COLOR = "#1e63d6"; // the app's trace blue
const START_COLOR = "#3e7c4f";
const END_COLOR = "#c2593f";
const MID_COLOR = "#1e63d6";
const WAYPOINT_COLOR = "#ce8a2e"; // theme.css --larkgold
const WAYPOINT_TEXT_COLOR = "#3b3226"; // theme.css --soilink

/** The app-drawn overlay's own label and marker sizes — the web half of
 * MapMarkings' markerScale/textScale/haloScale (map/mapMarkings.ts). These
 * are the values this file has always painted; they live as named
 * constants now because applyOverlayMarkings (below) has to be able to
 * recompute them from the base, not just nudge whatever is currently on
 * the layer — a rider going Bold -> High contrast -> Standard has to land
 * back on exactly these numbers, not on a number that drifted through two
 * multiplications. Everything NOT listed here is deliberately untouched:
 * the control points, ghost, magnetize flash and hover-scrub cursor are
 * draw-mode POINTER TARGETS sized for the hand, not map markings sized for
 * the eye, and growing them would move every hit test in the planner. */
const WAYPOINT_RADIUS = 6;
const WAYPOINT_LABEL_SIZE = 11;
const WAYPOINT_LABEL_HALO = 1.75;
const DAYBREAK_RADIUS = 8;
const DAYBREAK_LABEL_SIZE = 10;

// ---------------------------------------------------------------------
// Color semantics — read this before adding or changing any paint value
// in this module. Two hexes carry fixed meanings, reused deliberately
// rather than each thing getting its own hue:
//   larkgold (#ce8a2e) = the user's OWN marks (waypoints) + moderate grade
//     intensity — "this is something you placed, or a middling number."
//   ember (#c2593f) = severity — steep grade, the selected-point halo, and
//     every "loud"/danger accent elsewhere in the app (PlanBar's warning
//     chips, theme.css --ember) share this one meaning.
// Seasonal closures are colored with NEITHER. A hue-coded closure drawn
// over a grade-tinted segment would either vanish into a moderate run
// (both larkgold) or misread as a steep one (both ember) — the exact
// collision this app used to have. Closures are PATTERN-coded instead: a
// two-layer barrier hatch (dark sleeve + light dashed ties, immediately
// below) that reads as alternating dark/light ties over ANY route tint
// (trace/grade-easy/-moderate/-steep) or basemap imagery underneath,
// never colliding with a hue because it never uses one.
// ---------------------------------------------------------------------
const CLOSURE_SLEEVE_COLOR = "#3b3226"; // theme.css --soilink — hatch's dark under-layer
const CLOSURE_TIE_COLOR = "#f6efe2"; // theme.css --paledust — hatch's light dashed ties

// Route line width by zoom — the ONE table every route-relative width in
// this module derives from (Sep 6 desktop legibility pass; was a flat 3.5).
// Linear between the two stops, clamped outside them (MapLibre's own
// interpolate semantics). Casing, outline, grade runs, and the closure
// sleeve/ties are all expressed as a DELTA off this so they can't drift
// apart the way hand-typed literals did before (tests/plan-routeoverlay
// .test.ts pins every delta at the stops, between them, and beyond them).
//
// NOT device-gated: this is map paint, not CSS, so unlike theme.css's
// desktop block it changes the phone's route line too (3.5 → 4.5/6). The
// 1.01:1 against BLM road blue is the same problem on a phone, and a media
// query on paint would have to be re-evaluated on resize/orientation, so
// it's global by design — docs/WEB_ACCESSIBILITY.md §8.4 records that.
export const ROUTE_WIDTH_STOPS: ReadonlyArray<readonly [zoom: number, width: number]> = [
  [8, 4.5],
  [14, 6],
];
/** How far a RouteWeight (routeLook.ts) shifts the ROUTE_WIDTH_STOPS curve
 * off its own baseline — Regular is 0 by construction (its 5pt sits
 * between the two ROUTE_WIDTH_STOPS literals, matching the curve this file
 * already used before RouteLook existed), so Regular reproduces today's
 * route line exactly; Thin/Bold/Heavy shift by their own px distance from
 * Regular's 5. Every width in this module — casing, outline, the closure
 * hatch, grade runs — derives from the SAME shifted curve (via
 * routeRelativeWidth/gradeWidth below) so they all stay proportioned to
 * the route at whatever weight is chosen, the same guarantee
 * ROUTE_WIDTH_STOPS already gave them across zoom. */
function weightDelta(weight: RouteWeight): number {
  return ROUTE_WEIGHTS[weight].px - ROUTE_WEIGHTS.regular.px;
}
/** A zoom-interpolated line-width expression `delta` px wider (or narrower)
 * than the route line at every stop, at the given RouteWeight (defaults to
 * Regular — see weightDelta above for why that reproduces the pre-RouteLook
 * curve unchanged). */
function routeRelativeWidth(delta: number, weight: RouteWeight = DEFAULT_ROUTE_LOOK.weight): unknown[] {
  const wDelta = weightDelta(weight);
  return ["interpolate", ["linear"], ["zoom"], ...ROUTE_WIDTH_STOPS.flatMap(([z, w]) => [z, w + wDelta + delta])];
}
/** Casing is route + 4 (2px of casing each side — cream or soilink,
 * whichever the rider's chosen colour calls for, see routeLook.ts's
 * ROUTE_COLORS); the outline sits 1px further out again (casing + 2). */
const CASING_WIDTH_DELTA = 4;
const OUTLINE_WIDTH_DELTA = CASING_WIDTH_DELTA + 2;
/** Closure hatch: dark sleeve route + 2, light ties route − 1 — see the
 * stacking comment in ensureSourcesAndLayers for what is actually visible
 * of it. */
const CLOSURE_SLEEVE_WIDTH_DELTA = 2;
const CLOSURE_TIE_WIDTH_DELTA = -1;
/** Grade runs replace the solid route line while GRADE is on, so easy must
 * equal the route at EVERY zoom (not just one stop) or toggling GRADE
 * visibly thins the line inside a casing sized for the plain route;
 * moderate/steep step wider as the non-color severity channel. Applied as
 * an interpolate-over-zoom whose stop outputs are the per-class match —
 * MapLibre only allows ["zoom"] as the input of a TOP-LEVEL interpolate/
 * step, so the zoom curve can't be nested inside the match instead. */
const GRADE_WIDTH_DELTAS = { easy: 0, moderate: 1, steep: 2.5 } as const;
/** Same weight parameter as routeRelativeWidth, for the same reason: grade
 * runs replace the solid route line while GRADE is on (see
 * ensureRouteOverlay's visibility contract), so "easy" must track whatever
 * weight the route itself is currently painted at or toggling GRADE would
 * visibly change the line's thickness inside a casing sized for the route. */
function gradeWidth(weight: RouteWeight = DEFAULT_ROUTE_LOOK.weight): unknown[] {
  const wDelta = weightDelta(weight);
  return [
    "interpolate",
    ["linear"],
    ["zoom"],
    ...ROUTE_WIDTH_STOPS.flatMap(([z, w0]) => {
      const w = w0 + wDelta;
      return [
        z,
        [
          "match",
          ["get", "cls"],
          "easy",
          w + GRADE_WIDTH_DELTAS.easy,
          "moderate",
          w + GRADE_WIDTH_DELTAS.moderate,
          "steep",
          w + GRADE_WIDTH_DELTAS.steep,
          w, // nodata (and any unrecognized class) — the plain route's width
        ],
      ];
    }),
  ];
}

// Grade-run colors — the same three-color language as ProfileStrip's band
// (theme.css --grade-easy/--grade-moderate/--grade-steep) but flattened to
// opaque hex: MapLibre paint values are static/expression, not living CSS,
// so a var() read isn't available here the way ProfileStrip's inline style
// attrs get it.
// Lightness-ORDERED grade ramp (Aug 27 colorblind audit): the old
// success/larkgold/ember triad collapsed easy-vs-steep to ΔE76 ≈ 5.6
// under protanopia — near the just-noticeable floor, at the exact
// safety boundary, with no on-map redundancy while the profile is
// closed. These keep the hue instincts (green = go, gold = middling,
// red-family = serious) but run monotonically dark-as-hard (luminance
// ~0.45 → ~0.31 → ~0.05), so the ramp survives every CVD type and a
// grayscale print; the width step below is the second, non-color
// channel. theme.css's --grade-* tokens mirror these (ProfileStrip).
const GRADE_EASY_COLOR = "#8fbf9c"; // pale sage — lightest
const GRADE_MODERATE_COLOR = "#ce8a2e"; // == --larkgold — middle
const GRADE_STEEP_COLOR = "#6e1f16"; // deep brick — darkest

// Minimal local GeoJSON shapes rather than the ambient `GeoJSON.*`
// namespace maplibre-gl's own .d.ts assumes (not guaranteed to resolve
// under this project's narrowed tsconfig `types` list) — cast at the
// MapLibre boundary the same way MapCanvas already does for style JSON
// (`buildStyle(...) as any`) rather than fighting the style-spec's deeply
// recursive expression types for layer paint values.
interface LineFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { mode: LegMode };
    geometry: { type: "LineString"; coordinates: LonLat[] };
  }[];
}
interface PointFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { i: number; role: "start" | "end" | "mid"; selected: boolean };
    geometry: { type: "Point"; coordinates: LonLat };
  }[];
}
/** Bare single-point FC — the ghost hover marker and the magnetize flash
 * carry no properties worth naming, just a position. */
interface SinglePointFC {
  type: "FeatureCollection";
  features: { type: "Feature"; properties: Record<string, never>; geometry: { type: "Point"; coordinates: LonLat } }[];
}
interface WaypointFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { i: number; name: string };
    geometry: { type: "Point"; coordinates: LonLat };
  }[];
}
/** Overnight markers — properties carry just the label text (day-break
 * FEATURES aren't hit-tested by index the way points/waypoints are, so no
 * `i`). */
interface DayBreakFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { label: string };
    geometry: { type: "Point"; coordinates: LonLat };
  }[];
}
/** One feature per grade run — `cls` drives ROUTE_GRADE_LAYER's data-driven
 * line-color (see ensureSourcesAndLayers). */
interface GradeFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: { cls: string };
    geometry: { type: "LineString"; coordinates: LonLat[] };
  }[];
}
/** One feature per closed-way polyline — both hatch layers
 * (ROUTE_CLOSURES_LAYER + ROUTE_CLOSURES_TIE_LAYER) read this SAME
 * geometry off one source, so no properties worth naming. */
interface ClosureFC {
  type: "FeatureCollection";
  features: {
    type: "Feature";
    properties: Record<string, never>;
    geometry: { type: "LineString"; coordinates: LonLat[] };
  }[];
}

const EMPTY_LINE_FC: LineFC = { type: "FeatureCollection", features: [] };
const EMPTY_POINT_FC: PointFC = { type: "FeatureCollection", features: [] };
const EMPTY_SINGLE_POINT_FC: SinglePointFC = { type: "FeatureCollection", features: [] };
const EMPTY_WAYPOINT_FC: WaypointFC = { type: "FeatureCollection", features: [] };
const EMPTY_DAYBREAK_FC: DayBreakFC = { type: "FeatureCollection", features: [] };
const EMPTY_GRADE_FC: GradeFC = { type: "FeatureCollection", features: [] };
const EMPTY_CLOSURE_FC: ClosureFC = { type: "FeatureCollection", features: [] };

/** One feature per leg — filtered separately by mode into the solid/dashed
 * layers (see ROUTE_LINE_LAYER/ROUTE_LINE_STRAIGHT_LAYER). A leg with fewer
 * than 2 coordinates (shouldn't happen — every leg has ≥2 endpoints — but
 * matches lineFC's old below-2-points guard) is dropped rather than
 * emitting a degenerate LineString. */
function legsFC(legs: RouteOverlayLeg[]): LineFC {
  return {
    type: "FeatureCollection",
    features: legs
      .filter((leg) => leg.coords.length >= 2)
      .map((leg) => ({
        type: "Feature" as const,
        properties: { mode: leg.mode },
        geometry: { type: "LineString" as const, coordinates: leg.coords },
      })),
  };
}

function pointFC(points: LonLat[], selected: number | null): PointFC {
  return {
    type: "FeatureCollection",
    features: points.map((coordinates, i) => ({
      type: "Feature" as const,
      properties: {
        i,
        role: (i === 0 ? "start" : i === points.length - 1 ? "end" : "mid") as "start" | "end" | "mid",
        selected: i === selected,
      },
      geometry: { type: "Point" as const, coordinates },
    })),
  };
}

function singlePointFC(at: LonLat): SinglePointFC {
  return {
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "Point", coordinates: at } }],
  };
}

/** `i` is the feature's index into `waypoints` — the same "properties.i
 * names the array index" contract pointFC uses, so App.tsx's right-click
 * hit-test can hand a hit feature straight to removeWaypoint(state, i). */
function waypointFC(waypoints: { at: LonLat; name: string }[]): WaypointFC {
  return {
    type: "FeatureCollection",
    features: waypoints.map((w, i) => ({
      type: "Feature" as const,
      properties: { i, name: w.name },
      geometry: { type: "Point" as const, coordinates: w.at },
    })),
  };
}

function dayBreakFC(marks: { at: LonLat; label: string }[]): DayBreakFC {
  return {
    type: "FeatureCollection",
    features: marks.map((m) => ({
      type: "Feature" as const,
      properties: { label: m.label },
      geometry: { type: "Point" as const, coordinates: m.at },
    })),
  };
}

/** Runs with fewer than 2 coordinates are dropped rather than emitting a
 * degenerate LineString — same guard legsFC applies to legs. */
function gradeFC(runs: { cls: string; coords: LonLat[] }[]): GradeFC {
  return {
    type: "FeatureCollection",
    features: runs
      .filter((run) => run.coords.length >= 2)
      .map((run) => ({
        type: "Feature" as const,
        properties: { cls: run.cls },
        geometry: { type: "LineString" as const, coordinates: run.coords },
      })),
  };
}

function closureFC(closures: LonLat[][]): ClosureFC {
  return {
    type: "FeatureCollection",
    features: closures
      .filter((coords) => coords.length >= 2)
      .map((coords) => ({
        type: "Feature" as const,
        properties: {},
        geometry: { type: "LineString" as const, coordinates: coords },
      })),
  };
}

function ensureSourcesAndLayers(map: maplibregl.Map): void {
  // A missing route line means a fresh map or a style swap that dropped
  // every plan layer; whatever gets re-added below carries its
  // creation-time paint (Cobalt/Regular), so applyRouteLook's memo of
  // "already painted" is stale and must go. Sited HERE rather than in
  // ensureRouteOverlay because setPlanGhost/flashSnap/setScrubPoint also
  // rebuild through this function, off pointermove, with no look to apply
  // — one of those can beat the next ensureRouteOverlay to the rebuild.
  if (!map.getLayer(ROUTE_LINE_LAYER)) {
    appliedLook.delete(map);
    // Same staleness, same fix, for the markings theme: the layers about to
    // be re-added carry their creation-time Standard sizes.
    appliedMarkings.delete(map);
  }
  if (!map.getSource(ROUTE_LINE_SOURCE)) {
    map.addSource(ROUTE_LINE_SOURCE, { type: "geojson", data: EMPTY_LINE_FC } as any);
  }
  if (!map.getSource(ROUTE_POINTS_SOURCE)) {
    map.addSource(ROUTE_POINTS_SOURCE, { type: "geojson", data: EMPTY_POINT_FC } as any);
  }
  if (!map.getSource(ROUTE_WAYPOINTS_SOURCE)) {
    map.addSource(ROUTE_WAYPOINTS_SOURCE, { type: "geojson", data: EMPTY_WAYPOINT_FC } as any);
  }
  if (!map.getSource(ROUTE_GHOST_SOURCE)) {
    map.addSource(ROUTE_GHOST_SOURCE, { type: "geojson", data: EMPTY_SINGLE_POINT_FC } as any);
  }
  if (!map.getSource(ROUTE_FLASH_SOURCE)) {
    map.addSource(ROUTE_FLASH_SOURCE, { type: "geojson", data: EMPTY_SINGLE_POINT_FC } as any);
  }
  if (!map.getSource(ROUTE_CLOSURES_SOURCE)) {
    map.addSource(ROUTE_CLOSURES_SOURCE, { type: "geojson", data: EMPTY_CLOSURE_FC } as any);
  }
  if (!map.getSource(ROUTE_GRADE_SOURCE)) {
    map.addSource(ROUTE_GRADE_SOURCE, { type: "geojson", data: EMPTY_GRADE_FC } as any);
  }
  if (!map.getSource(ROUTE_DAYBREAKS_SOURCE)) {
    map.addSource(ROUTE_DAYBREAKS_SOURCE, { type: "geojson", data: EMPTY_DAYBREAK_FC } as any);
  }
  if (!map.getSource(ROUTE_SCRUB_SOURCE)) {
    map.addSource(ROUTE_SCRUB_SOURCE, { type: "geojson", data: EMPTY_SINGLE_POINT_FC } as any);
  }

  // Added in order, each on TOP (no beforeId) — closures (dark sleeve then
  // light ties) under the dark outline under the cream casing under
  // solid/grade line under dashed line under control points under named
  // waypoints under day-break markers under the hover ghost under the
  // transient flash under the scrub cursor, all above every basemap layer.
  //
  // Widths, outermost first, all off ROUTE_WIDTH_STOPS (z8 → z14) AT THE
  // DEFAULT Regular weight — every row shifts with the rider's chosen
  // weight, together and by the same px, since they all derive from
  // routeRelativeWidth (see weightDelta):
  //   outline  route + 6   10.5 → 12   soilink @ 0.55  (snapped legs only)
  //   casing   route + 4    8.5 → 10   cream/soilink per colour @ 0.9
  //   sleeve   route + 2    6.5 →  8   soilink @ 0.85  (closure hatch)
  //   route               4.5 →  6   the rider's RouteLook colour
  //   ties     route − 1    3.5 →  5   paledust, dashed (closure hatch)
  //   grade    easy = route, moderate + 1, steep + 2.5 (replaces route)
  // What the closure hatch actually shows: a closure feature is the WHOLE
  // closed way (seasons.ts hands over entry.p), and where the route runs
  // along it the outline + casing above are both wider than the sleeve and
  // near-opaque, so under the route the hatch is covered (≈4.5% of the
  // sleeve's edge leaks through 0.9 × 0.55). The visible hatch is the
  // closed way's extent BEYOND the route — the ties leading away from
  // where the route joins/leaves it. The sleeve ≥ route delta matters at
  // that seam: the tail continues at no less than the route's own weight,
  // so it reads as the same way carrying on rather than a lesser line; and
  // ties < sleeve keeps the light dashes inside the dark band so they read
  // as hatch marks, never a second line alongside.
  if (!map.getLayer(ROUTE_CLOSURES_LAYER)) {
    // FIRST — bottom of the whole plan stack, so the route itself always
    // reads on top of the ways it's warning about, never the other way
    // around. This is the barrier hatch's dark UNDER-layer (see the
    // color-semantics comment above CLOSURE_SLEEVE_COLOR) — deliberately
    // NOT larkgold/ember, so it can never collide with a route tint or a
    // waypoint painted above it.
    map.addLayer({
      id: ROUTE_CLOSURES_LAYER,
      type: "line",
      source: ROUTE_CLOSURES_SOURCE,
      paint: {
        "line-color": CLOSURE_SLEEVE_COLOR,
        // route width + 2 at every zoom stop — see the width table above
        "line-width": routeRelativeWidth(CLOSURE_SLEEVE_WIDTH_DELTA),
        "line-opacity": 0.85,
      },
    } as any);
  }
  // The hatch's OVER-layer — short light dashes on the SAME source, added
  // immediately above the sleeve so together they read as alternating
  // dark/light barrier ties (think: a railroad crossing gate), legible
  // over any route color or satellite imagery beneath — a pattern, not a
  // hue, is what makes that true regardless of what's underneath.
  if (!map.getLayer(ROUTE_CLOSURES_TIE_LAYER)) {
    map.addLayer({
      id: ROUTE_CLOSURES_TIE_LAYER,
      type: "line",
      source: ROUTE_CLOSURES_SOURCE,
      paint: {
        "line-color": CLOSURE_TIE_COLOR,
        "line-width": routeRelativeWidth(CLOSURE_TIE_WIDTH_DELTA), // route width − 1
        "line-dasharray": [1.5, 1.5],
      },
    } as any);
  }
  // Dark outer outline — directly under the casing, snapped legs only (the
  // dashed straight legs keep just the casing). See
  // ROUTE_LINE_OUTLINE_LAYER's doc comment for the contrast measurements
  // that put it here and why it skips the dashed legs.
  if (!map.getLayer(ROUTE_LINE_OUTLINE_LAYER)) {
    map.addLayer({
      id: ROUTE_LINE_OUTLINE_LAYER,
      type: "line",
      source: ROUTE_LINE_SOURCE,
      filter: ["==", ["get", "mode"], "snapped"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": CLOSURE_SLEEVE_COLOR, // == theme.css --soilink
        "line-width": routeRelativeWidth(OUTLINE_WIDTH_DELTA),
        "line-opacity": 0.55,
      },
    } as any);
  }
  if (!map.getLayer(ROUTE_LINE_CASING_LAYER)) {
    map.addLayer({
      id: ROUTE_LINE_CASING_LAYER,
      type: "line",
      source: ROUTE_LINE_SOURCE,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": CASING_COLOR,
        "line-width": routeRelativeWidth(CASING_WIDTH_DELTA),
        "line-opacity": 0.9,
      },
    } as any);
  }
  // Solid: on the legal network. Honest per docs/WEB_PLANNER_UX_REVIEW.md —
  // never claims a snap it doesn't have.
  if (!map.getLayer(ROUTE_LINE_LAYER)) {
    map.addLayer({
      id: ROUTE_LINE_LAYER,
      type: "line",
      source: ROUTE_LINE_SOURCE,
      filter: ["==", ["get", "mode"], "snapped"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": TRACE_COLOR, "line-width": routeRelativeWidth(0) },
    } as any);
  }
  // Grade-colored replacement for the solid line above — added immediately
  // after it so it sits ABOVE the solid line but BELOW the dashed/halo/
  // points layers. Only one of this and ROUTE_LINE_LAYER is ever visible
  // at a time; see ensureRouteOverlay's visibility contract.
  if (!map.getLayer(ROUTE_GRADE_LAYER)) {
    map.addLayer({
      id: ROUTE_GRADE_LAYER,
      type: "line",
      source: ROUTE_GRADE_SOURCE,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: {
        // Width steps with severity — the non-color channel backing the
        // lightness-ordered ramp above (a steep run reads wider even in
        // grayscale; ownership's own-outline width steps set the
        // precedent for width-as-category on this map). easy == the plain
        // route at every zoom, moderate +1, steep +2.5 (see gradeWidth):
        // 4.5/5.5/7 @ z8 → 6/7/8.5 @ z14, steep still inside the casing.
        // Sep 6: was a flat 3.5/4/5 alongside the route's flat 3.5.
        "line-width": gradeWidth(),
        "line-color": [
          "match",
          ["get", "cls"],
          "easy",
          GRADE_EASY_COLOR,
          "moderate",
          GRADE_MODERATE_COLOR,
          "steep",
          GRADE_STEEP_COLOR,
          // nodata (and any unrecognized class) — the plain route's own
          // colour, so an unsampled run reads as "route, no grade data"
          // rather than a seventh category. This literal is only the
          // creation-time default: applyRouteLook rewrites the whole match
          // so this arm tracks the rider's chosen hue (Sep 14 2026 — it
          // was left behind on the first pass, which painted an unsampled
          // run cobalt in the middle of an Amber route).
          TRACE_COLOR,
        ],
      },
    } as any);
  }
  // Dashed: NOT on the road network, said plainly rather than guessed at.
  if (!map.getLayer(ROUTE_LINE_STRAIGHT_LAYER)) {
    map.addLayer({
      id: ROUTE_LINE_STRAIGHT_LAYER,
      type: "line",
      source: ROUTE_LINE_SOURCE,
      filter: ["==", ["get", "mode"], "straight"],
      layout: { "line-cap": "round", "line-join": "round" },
      // dasharray is in line-width units: [2, 2] at 4.5-6px is a 9-12px
      // dash/gap — still clearly dashed (the round caps eat ~half a width
      // of each gap, leaving ≥6px of daylight at z14), so the pattern
      // stays; a shorter [1.6, 1.4] would shrink the GAP too and read
      // closer to solid, not less so.
      paint: { "line-color": TRACE_COLOR, "line-width": routeRelativeWidth(0), "line-dasharray": [2, 2] },
    } as any);
  }
  // Selection halo, added BEFORE the points layer so it stacks underneath —
  // filtered to just the selected feature; MapLibre filter expressions
  // accept any boolean-valued expression, so the bare boolean property
  // reads directly without an explicit `== true` comparison.
  if (!map.getLayer(ROUTE_POINTS_HALO_LAYER)) {
    map.addLayer({
      id: ROUTE_POINTS_HALO_LAYER,
      type: "circle",
      source: ROUTE_POINTS_SOURCE,
      filter: ["get", "selected"],
      paint: {
        "circle-radius": 13,
        "circle-color": "#c2593f",
        "circle-opacity": 0.35,
      },
    } as any);
  }
  if (!map.getLayer(ROUTE_POINTS_LAYER)) {
    map.addLayer({
      id: ROUTE_POINTS_LAYER,
      type: "circle",
      source: ROUTE_POINTS_SOURCE,
      paint: {
        "circle-radius": ["case", ["get", "selected"], 9, 5.5],
        "circle-color": ["match", ["get", "role"], "start", START_COLOR, "end", END_COLOR, MID_COLOR],
        "circle-stroke-color": CASING_COLOR,
        "circle-stroke-width": ["case", ["get", "selected"], 3, 2],
      },
    } as any);
  }
  // Named landmarks (Gate, Water crossing, …) — a filled larkgold circle
  // plus its name floating above, on the SAME source (properties {i,
  // name}) so one queryRenderedFeatures against ROUTE_WAYPOINTS_LAYER
  // hit-tests both at once. Added after the control points so a waypoint
  // dropped near one still reads on top of it.
  if (!map.getLayer(ROUTE_WAYPOINTS_LAYER)) {
    map.addLayer({
      id: ROUTE_WAYPOINTS_LAYER,
      type: "circle",
      source: ROUTE_WAYPOINTS_SOURCE,
      paint: {
        "circle-radius": WAYPOINT_RADIUS,
        "circle-color": WAYPOINT_COLOR,
        "circle-stroke-color": CASING_COLOR,
        "circle-stroke-width": 2,
      },
    } as any);
  }
  if (!map.getLayer(ROUTE_WAYPOINTS_LABEL_LAYER)) {
    map.addLayer({
      id: ROUTE_WAYPOINTS_LABEL_LAYER,
      type: "symbol",
      source: ROUTE_WAYPOINTS_SOURCE,
      layout: {
        "text-field": ["get", "name"],
        // Matches template.json's own symbol layers exactly (verified
        // against poi-label/peak-label/water-name, all "Noto-Sans-Regular")
        // — the style's glyph set, not a guess.
        "text-font": ["Noto-Sans-Regular"],
        "text-size": WAYPOINT_LABEL_SIZE,
        "text-offset": [0, -1.2],
        "text-anchor": "bottom",
      },
      paint: {
        "text-color": WAYPOINT_TEXT_COLOR,
        "text-halo-color": CASING_COLOR,
        "text-halo-width": WAYPOINT_LABEL_HALO,
      },
    } as any);
  }
  // Overnight markers — added after the waypoint label layer so a day
  // break sitting near a named waypoint still reads on top of it. Dark
  // ink fill (soilink, same as waypoint label text) rather than the
  // trail's own palette, so an overnight marker never gets mistaken for a
  // route-network cue.
  if (!map.getLayer(ROUTE_DAYBREAKS_LAYER)) {
    map.addLayer({
      id: ROUTE_DAYBREAKS_LAYER,
      type: "circle",
      source: ROUTE_DAYBREAKS_SOURCE,
      paint: {
        "circle-radius": DAYBREAK_RADIUS,
        "circle-color": WAYPOINT_TEXT_COLOR,
        "circle-stroke-color": CASING_COLOR,
        "circle-stroke-width": 2,
      },
    } as any);
  }
  if (!map.getLayer(ROUTE_DAYBREAKS_LABEL_LAYER)) {
    map.addLayer({
      id: ROUTE_DAYBREAKS_LABEL_LAYER,
      type: "symbol",
      source: ROUTE_DAYBREAKS_SOURCE,
      layout: {
        "text-field": ["get", "label"],
        // Same glyph set as every other symbol layer here — see the
        // waypoint label layer's comment above.
        "text-font": ["Noto-Sans-Regular"],
        "text-size": DAYBREAK_LABEL_SIZE,
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": CASING_COLOR,
      },
    } as any);
  }
  // Insert-on-line hover preview (docs/WEB_PLANNER_UX_REVIEW.md P1 #5) —
  // translucent so it never reads as a committed point.
  if (!map.getLayer(ROUTE_GHOST_LAYER)) {
    map.addLayer({
      id: ROUTE_GHOST_LAYER,
      type: "circle",
      source: ROUTE_GHOST_SOURCE,
      paint: {
        "circle-radius": 6,
        "circle-color": MID_COLOR,
        "circle-opacity": 0.45,
        "circle-stroke-color": CASING_COLOR,
        "circle-stroke-width": 1.5,
      },
    } as any);
  }
  // Magnetize flash (delight rider, same doc) — flashSnap animates its
  // radius/opacity; the base paint here is just its resting (invisible,
  // empty-data) state.
  if (!map.getLayer(ROUTE_FLASH_LAYER)) {
    map.addLayer({
      id: ROUTE_FLASH_LAYER,
      type: "circle",
      source: ROUTE_FLASH_SOURCE,
      paint: {
        "circle-radius": 6,
        "circle-color": TRACE_COLOR,
        "circle-opacity": 0.6,
        "circle-stroke-width": 0,
      },
    } as any);
  }
  // LAST — topmost of the whole plan stack: ProfileStrip's hover-scrub
  // cursor tracks the pointer and must never read as occluded by anything
  // else drawn here.
  if (!map.getLayer(ROUTE_SCRUB_LAYER)) {
    map.addLayer({
      id: ROUTE_SCRUB_LAYER,
      type: "circle",
      source: ROUTE_SCRUB_SOURCE,
      paint: {
        "circle-radius": 7,
        "circle-color": TRACE_COLOR,
        "circle-stroke-color": CASING_COLOR,
        "circle-stroke-width": 2.5,
      },
    } as any);
  }
}

/** Re-applies the CURRENT RouteLook (routeLook.ts) to the plan line and
 * every width that derives from it — casing, outline, the closure hatch,
 * grade runs — on any ensureRouteOverlay pass where it CHANGED, not just
 * the first one that creates the layers. Layer creation above is
 * idempotent (add once, never again), but Lee can change Colour/Weight
 * from the LOOK menu at any time, so repainting can't wait for a layer to
 * not exist yet. Uses setPaintProperty exactly the way flashSnap (below)
 * already updates paint on a live layer without touching source/layer
 * identity — no map.setStyle, no addLayer/removeLayer churn. Dash patterns
 * (the off-network straight layer, the closure ties) are untouched —
 * Colour/Weight restyle the line, they never change what's dashed and what
 * isn't. */
const appliedLook = new WeakMap<maplibregl.Map, RouteLook>();

function applyRouteLook(map: maplibregl.Map, look: RouteLook): void {
  // ensureRouteOverlay runs off App.tsx's `planner` effect, which fires on
  // EVERY drag-preview mousemove frame (see the debounced-autosave comment
  // there) — but a RouteLook changes a handful of times a session. Without
  // this guard every one of those frames re-parsed and re-bound ten paint
  // expressions, eight of them zoom interpolates, for no pixel change.
  // Invalidated in ensureSourcesAndLayers when the layers are rebuilt.
  const prev = appliedLook.get(map);
  if (prev && prev.color === look.color && prev.weight === look.weight) return;
  appliedLook.set(map, look);

  const { hex, casing } = ROUTE_COLORS[look.color];
  const routeWidth = routeRelativeWidth(0, look.weight);
  map.setPaintProperty(ROUTE_LINE_LAYER, "line-color", hex);
  map.setPaintProperty(ROUTE_LINE_LAYER, "line-width", routeWidth);
  map.setPaintProperty(ROUTE_LINE_STRAIGHT_LAYER, "line-color", hex);
  map.setPaintProperty(ROUTE_LINE_STRAIGHT_LAYER, "line-width", routeWidth);
  // Casing swaps cream/soilink per the chosen colour's own brightness (see
  // ROUTE_COLORS) — the dark outer outline UNDER it stays soilink
  // regardless, since it's already dark and untouched by this setting.
  map.setPaintProperty(ROUTE_LINE_CASING_LAYER, "line-color", casing);
  map.setPaintProperty(ROUTE_LINE_CASING_LAYER, "line-width", routeRelativeWidth(CASING_WIDTH_DELTA, look.weight));
  map.setPaintProperty(ROUTE_LINE_OUTLINE_LAYER, "line-width", routeRelativeWidth(OUTLINE_WIDTH_DELTA, look.weight));
  map.setPaintProperty(
    ROUTE_CLOSURES_LAYER,
    "line-width",
    routeRelativeWidth(CLOSURE_SLEEVE_WIDTH_DELTA, look.weight),
  );
  map.setPaintProperty(
    ROUTE_CLOSURES_TIE_LAYER,
    "line-width",
    routeRelativeWidth(CLOSURE_TIE_WIDTH_DELTA, look.weight),
  );
  map.setPaintProperty(ROUTE_GRADE_LAYER, "line-width", gradeWidth(look.weight));
  // The grade layer's THREE severity colours are data, not decoration, and
  // stay put — but its fourth arm (nodata / unrecognized class) is the
  // plain route, so it has to follow the rider too. Left out on the first
  // pass, which meant a GRADE-on route with any unsampled run painted that
  // run cobalt through the middle of an Amber line.
  map.setPaintProperty(ROUTE_GRADE_LAYER, "line-color", [
    "match",
    ["get", "cls"],
    "easy",
    GRADE_EASY_COLOR,
    "moderate",
    GRADE_MODERATE_COLOR,
    "steep",
    GRADE_STEEP_COLOR,
    hex,
  ]);
}

/** Re-applies the CURRENT map-markings theme (map/mapMarkings.ts) to the
 * overlay's own labels and marker art — the app-drawn half of the setting,
 * the half buildStyle can't reach because these layers are added here at
 * runtime, not baked into the style JSON.
 *
 * Same mechanics as applyRouteLook directly above, for the same reasons:
 * memoised per map so the per-mousemove ensure passes don't re-bind
 * anything, invalidated in ensureSourcesAndLayers when a style swap drops
 * the layers back to their creation-time paint. Every value is recomputed
 * from the module's base constants (WAYPOINT_RADIUS et al) rather than from
 * whatever is on the layer, so switching themes can't compound.
 *
 * text-size is a LAYOUT property, not paint — a detail worth stating,
 * because everything else this file restyles at runtime goes through
 * setPaintProperty and a setPaintProperty("text-size", …) fails silently
 * rather than loudly. */
const appliedMarkings = new WeakMap<maplibregl.Map, MapMarkings>();

function applyOverlayMarkings(map: maplibregl.Map, markings: MapMarkings): void {
  const prev = appliedMarkings.get(map);
  if (prev === markings) return;
  appliedMarkings.set(map, markings);

  const m = MARKINGS[markings] ?? MARKINGS[DEFAULT_MARKINGS];
  map.setPaintProperty(ROUTE_WAYPOINTS_LAYER, "circle-radius", WAYPOINT_RADIUS * m.markerScale);
  map.setLayoutProperty(ROUTE_WAYPOINTS_LABEL_LAYER, "text-size", WAYPOINT_LABEL_SIZE * m.textScale);
  map.setPaintProperty(
    ROUTE_WAYPOINTS_LABEL_LAYER,
    "text-halo-width",
    WAYPOINT_LABEL_HALO * m.haloScale,
  );
  map.setPaintProperty(ROUTE_DAYBREAKS_LAYER, "circle-radius", DAYBREAK_RADIUS * m.markerScale);
  map.setLayoutProperty(ROUTE_DAYBREAKS_LABEL_LAYER, "text-size", DAYBREAK_LABEL_SIZE * m.textScale);
}

/** Idempotent: builds the plan-* sources+layers if a style swap (or the
 * very first draw) wiped or never had them, then always pushes the latest
 * line/point geometry. `active:false` empties both sources rather than
 * removing the layers — empty data renders nothing and is simpler than
 * churning addLayer/removeLayer on every draw-mode toggle. The ghost/flash
 * layers are NOT driven by `view` — App calls setPlanGhost/flashSnap
 * directly off hover/snap events — but their sources/layers are still
 * created here (see ensureSourcesAndLayers) so a style swap never leaves
 * them missing until the next hover/flash happens to fire.
 *
 * Returns whether it actually painted; a false return tells the caller to
 * retry (App polls on a timer until the first success).
 *
 * Deliberately NO isStyleLoaded() gate: that predicate also tracks TILE
 * loading, and a slow or wedged raster source (hillshade/lidar on a cold
 * cache) kept it false for entire sessions in the field — the overlay
 * starved even though the style graph was long since ready. Painting
 * needs the parsed style, not the tiles: addSource/addLayer throw only
 * while the style JSON itself is mid-parse, and the catch below turns
 * that brief window into a retry.
 *
 * `routeLook` defaults to DEFAULT_ROUTE_LOOK (Cobalt/Regular) — a caller
 * that hasn't wired up the Colour/Weight setting yet (or a test, like
 * tests/plan-routeoverlay.test.ts, that never mentions RouteLook at all)
 * gets exactly the route line this file painted before RouteLook existed,
 * unchanged.
 *
 * `markings` defaults to `standard` on the same terms — the overlay's
 * labels and marker art come out at exactly the sizes above, so a caller
 * (or a test) that never mentions map markings sees nothing move. */
export function ensureRouteOverlay(
  map: maplibregl.Map,
  view: RouteOverlayView,
  routeLook: RouteLook = DEFAULT_ROUTE_LOOK,
  markings: MapMarkings = DEFAULT_MARKINGS,
): boolean {
  try {
    ensureSourcesAndLayers(map);
    applyRouteLook(map, routeLook);
    applyOverlayMarkings(map, markings);
    const line = view.active ? legsFC(view.legs) : EMPTY_LINE_FC;
    const points = view.active ? pointFC(view.points, view.selected) : EMPTY_POINT_FC;
    const waypoints = view.active ? waypointFC(view.waypoints) : EMPTY_WAYPOINT_FC;
    const closures = view.active ? closureFC(view.closures ?? []) : EMPTY_CLOSURE_FC;
    const grade = view.active ? gradeFC(view.gradeRuns ?? []) : EMPTY_GRADE_FC;
    const dayBreaks = view.active ? dayBreakFC(view.dayBreaks ?? []) : EMPTY_DAYBREAK_FC;

    map.getSource<maplibregl.GeoJSONSource>(ROUTE_LINE_SOURCE)?.setData(line as any);
    map.getSource<maplibregl.GeoJSONSource>(ROUTE_POINTS_SOURCE)?.setData(points as any);
    map.getSource<maplibregl.GeoJSONSource>(ROUTE_WAYPOINTS_SOURCE)?.setData(waypoints as any);
    map.getSource<maplibregl.GeoJSONSource>(ROUTE_CLOSURES_SOURCE)?.setData(closures as any);
    map.getSource<maplibregl.GeoJSONSource>(ROUTE_GRADE_SOURCE)?.setData(grade as any);
    map.getSource<maplibregl.GeoJSONSource>(ROUTE_DAYBREAKS_SOURCE)?.setData(dayBreaks as any);
    // Scrub is CLEAR-ONLY here — setScrubPoint (below) is the sole writer
    // of LIVE cursor data, called directly off pointermove. Funding that
    // through this ensure pass instead (state -> render -> effect ->
    // ensure) would cap a 60 Hz hover gesture at React's render cadence.
    // The one thing this pass DOES own is blanking the source on leaving
    // draw mode, so a cursor from the last session's hover can't survive
    // into a fresh one.
    if (!view.active) {
      map.getSource<maplibregl.GeoJSONSource>(ROUTE_SCRUB_SOURCE)?.setData(EMPTY_SINGLE_POINT_FC as any);
    }

    // gradeOn && gradeRuns non-empty swaps the solid on-network line for
    // its grade-colored twin; the dashed straight layer is NEVER hidden —
    // grade coloring only ever replaces the solid line, never the honesty
    // cue that a leg isn't on the network.
    const gradeVisible = !!(view.gradeOn && view.gradeRuns && view.gradeRuns.length > 0);
    map.setLayoutProperty(ROUTE_LINE_LAYER, "visibility", gradeVisible ? "none" : "visible");
    map.setLayoutProperty(ROUTE_GRADE_LAYER, "visibility", gradeVisible ? "visible" : "none");

    return true;
  } catch {
    // Style JSON mid-parse (or a swap mid-transition) — report the
    // dropped frame so the caller's timer retries.
    return false;
  }
}

/** Shows (at a coordinate) or clears (null) the insert-on-line hover
 * ghost. Safe to call before the map's first ensureRouteOverlay success —
 * it ensures its own sources/layers — and cheap enough to call on every
 * throttled mousemove tick in App's draw-mode handler. */
export function setPlanGhost(map: maplibregl.Map, at: LonLat | null): void {
  try {
    ensureSourcesAndLayers(map);
  } catch {
    return; // style mid-parse — next mousemove tick tries again
  }
  map
    .getSource<maplibregl.GeoJSONSource>(ROUTE_GHOST_SOURCE)
    ?.setData((at ? singlePointFC(at) : EMPTY_SINGLE_POINT_FC) as any);
}

/** Shows (at a coordinate) or clears (null) ProfileStrip's hover-scrub
 * cursor on the map — mirrors setPlanGhost exactly, same reason: cheap
 * enough to call on every throttled pointermove tick, safe to call before
 * the map's first ensureRouteOverlay success. This (not ensureRouteOverlay
 * — see its CLEAR-ONLY handling of ROUTE_SCRUB_SOURCE above) is the scrub
 * source's one live writer. */
export function setScrubPoint(map: maplibregl.Map, at: LonLat | null): void {
  try {
    ensureSourcesAndLayers(map);
  } catch {
    return; // style mid-parse — next pointermove tick tries again
  }
  map
    .getSource<maplibregl.GeoJSONSource>(ROUTE_SCRUB_SOURCE)
    ?.setData((at ? singlePointFC(at) : EMPTY_SINGLE_POINT_FC) as any);
}

/** One in-flight flash at a time — a second snap arriving mid-animation
 * cancels the first and restarts clean rather than fighting it for the
 * layer's paint properties. Module-level rather than per-map since the
 * app only ever drives one live map. */
let flashHandle: ReturnType<typeof requestAnimationFrame> | null = null;

const FLASH_DURATION_MS = 350;
// Paint updates are throttled to real elapsed time, not every rAF tick —
// ~7 updates over the full duration is plenty for a 350 ms ring and cheaper
// than pushing a style property every frame.
const FLASH_STEP_MS = 50;
const FLASH_START_RADIUS = 6;
const FLASH_END_RADIUS = 26;
const FLASH_START_OPACITY = 0.6;

/** Transient "magnetize" ring at a snap acceptance
 * (docs/WEB_PLANNER_UX_REVIEW.md's delight rider) — expands and fades over
 * ~350 ms, then clears. Cheap (a handful of setPaintProperty calls, no CSS
 * dependency) and, per the module doc above, survives a style swap mid-
 * animation the same way the rest of the overlay does. */
export function flashSnap(map: maplibregl.Map, at: LonLat): void {
  try {
    ensureSourcesAndLayers(map);
  } catch {
    return; // style mid-parse — a missed flash is cosmetic, not worth retrying
  }
  const source = map.getSource<maplibregl.GeoJSONSource>(ROUTE_FLASH_SOURCE);
  if (!source) return;

  if (flashHandle !== null) {
    cancelAnimationFrame(flashHandle);
    flashHandle = null;
  }
  source.setData(singlePointFC(at) as any);

  const start = performance.now();
  let lastStep = -1;
  const tick = (now: number) => {
    const elapsed = now - start;
    const t = Math.min(1, elapsed / FLASH_DURATION_MS);
    const step = Math.floor(elapsed / FLASH_STEP_MS);
    if (step !== lastStep) {
      lastStep = step;
      map.setPaintProperty(ROUTE_FLASH_LAYER, "circle-radius", FLASH_START_RADIUS + t * (FLASH_END_RADIUS - FLASH_START_RADIUS));
      map.setPaintProperty(ROUTE_FLASH_LAYER, "circle-opacity", FLASH_START_OPACITY * (1 - t));
    }
    if (t >= 1) {
      flashHandle = null;
      source.setData(EMPTY_SINGLE_POINT_FC as any);
    } else {
      flashHandle = requestAnimationFrame(tick);
    }
  };
  flashHandle = requestAnimationFrame(tick);
}
